"use client";

import { FC, useEffect, useRef, useState } from "react";
import cn from "classnames";
import {
  SecondaryNavigation as SecondaryNavigationProps,
  SecondaryNavigationVariant,
} from "../types";
import Link from "@/components/Link";
import Dropdown from "./Dropdown";
import { PopoverGroup } from "@headlessui/react";
import { Button } from "../../Button";

const styles: Record<
  SecondaryNavigationVariant,
  {
    inner: string;
    group: string;
    dropdownLink: string;
    rightColumn: string;
  }
> = {
  default: {
    inner:
      "swdc-container relative flex h-full min-h-13 items-center justify-end py-2 lg:px-6",
    group: "flex h-full gap-x-8",
    dropdownLink:
      "text-link-m text-swdc-black-600 active:text-swdc-blue-500 hover:after:bg-swdc-silver-400 focus:after:bg-swdc-silver-400 active:after:bg-swdc-silver-400 relative flex h-full items-center font-medium outline-none after:absolute after:right-0 after:-bottom-2 after:left-0 after:h-0.5 after:rounded-full after:bg-transparent after:content-['']",
    rightColumn: "ml-8 flex items-center gap-x-4 whitespace-nowrap",
  },
  redesigned: {
    inner:
      "swdc-container relative flex min-h-13 items-center gap-x-14 py-2 lg:px-4",
    group: "flex items-center gap-x-5",
    dropdownLink:
      "text-link-xs text-swdc-black-600 focus-visible:outline-swdc-focus-primary hover:bg-swdc-silver-300 active:bg-swdc-silver-300 flex items-center rounded-xs px-1 py-0.5 capitalize outline-2 outline-offset-0 outline-transparent focus:not-focus-visible:outline-none transition ease-out duration-100",
    rightColumn: "ml-auto flex items-center gap-x-4 whitespace-nowrap",
  },
};

const SecondaryNavigation: FC<SecondaryNavigationProps> = ({
  enableProductLink = true,
  newSecondaryNavigation = false,
  ...props
}) => {
  const variant: SecondaryNavigationVariant = newSecondaryNavigation
    ? "redesigned"
    : "default";
  const isRedesigned = variant === "redesigned";
  const s = styles[variant];

  const navRef = useRef<HTMLDivElement | null>(null);
  const [isStickyTop, setIsStickyTop] = useState<boolean>(
    props.isStickyTop || false,
  );
  const [isAnyDropdownOpen, setIsAnyDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (navRef.current) {
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e) {
            setIsStickyTop(
              props.isStickyTop ||
                (!e.isIntersecting && e.boundingClientRect.top <= 0),
            );
          }
        },
        {
          threshold: [1],
          rootMargin: "-1px 0px 0px 0px",
        },
      );
      observer.observe(navRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [props.isStickyTop]);

  return (
    <div ref={navRef} className={isRedesigned ? "pt-1" : ""}>
      <div
        className={
          isRedesigned
            ? cn(
                "min-h-13",
                isAnyDropdownOpen
                  ? "bg-swdc-cool-gray-200"
                  : "bg-swdc-cool-gray-200/70 backdrop-blur-[5px]",
              )
            : "bg-swdc-silver-200 border-swdc-gray-200 min-h-13 border-b-1"
        }
      >
        <div className={s.inner}>
          {isRedesigned
            ? props.productLink?.title &&
              (enableProductLink && props.productLink.href ? (
                <Link
                  href={props.productLink.href}
                  className="text-link-xs text-swdc-black-600 shrink-0 origin-center font-bold whitespace-nowrap capitalize transition duration-100 ease-out hover:underline active:scale-[0.98]"
                  dld={props.productLink.dld}
                  dlt={props.productLink.dlt}
                >
                  {props.productLink.title}
                </Link>
              ) : (
                <span className="text-link-xs text-swdc-black-600 shrink-0 font-bold whitespace-nowrap capitalize">
                  {props.productLink.title}
                </span>
              ))
            : props.productLink?.title &&
              props.productLink?.href &&
              (enableProductLink ? (
                <Button
                  variant="tertiary"
                  iconPosition="left"
                  icon="arrowLeft"
                  href={props.productLink.href}
                  className="text-link-m text-swdc-black-600 hover:text-swdc-black-600 active:text-swdc-blue-500 mr-auto -ml-1 justify-start font-bold"
                  dld={props.productLink.dld}
                  dlt={props.productLink.dlt}
                >
                  {props.productLink.title}
                </Button>
              ) : (
                <span className="text-link-m text-swdc-black-600 mr-auto font-bold">
                  {props.productLink.title}
                </span>
              ))}
          <PopoverGroup as="ul" className={s.group}>
            {props.dropdowns?.map((dropdown, index) =>
              dropdown.href ? (
                <li key={`${dropdown.title}-${index}`}>
                  <Link
                    href={dropdown.href}
                    className={s.dropdownLink}
                    dld={dropdown.dld}
                    dlt={dropdown.dlt}
                  >
                    {dropdown.title}
                  </Link>
                </li>
              ) : (
                <Dropdown
                  key={`${dropdown.title}-${index}`}
                  dropdown={dropdown}
                  variant={variant}
                  onOpenChange={isRedesigned ? setIsAnyDropdownOpen : undefined}
                />
              ),
            )}
          </PopoverGroup>
          {props.rightColumn && (
            <div className={s.rightColumn}>
              {isStickyTop && props.rightColumn.visibleWhenSticky}
              {props.rightColumn.alwaysVisible}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecondaryNavigation;
